import { Box } from '@lifeomic/chroma-react/components/Box';
import { defineMessages, useIntl } from 'react-intl';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useCustomCompareEffect } from 'react-use';
import React from 'react';

import { SurveyOverrideClasses } from '../SurveyOverrideClasses';
import { useNotify } from '../../../../hooks';
import { useSurveyResponse } from './useSurveyResponse';
import Survey from '../Survey';
import SurveyAutopopulateContainer from '../SurveyAutopopulateContainer';

const messages = defineMessages({
  readError: {
    id: 'subjectResponseViewer.focusedSurvey.error',
    defaultMessage: 'An unexpected error occurred',
  },
  writeError: {
    id: 'subjectResponseViewer.focusedSurvey.writeError',
    defaultMessage:
      'An unexpected error occurred while saving your changes. Try closing and reopening the survey.',
  },
});

const useStyles = makeStyles(() => ({
  paddedContainer: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  alert: {
    marginBottom: '1rem',
  },
}));

type FocusedSurveyProps = {
  responseId: string;
  onSurveyCompletion: (didCompleteSurvey: boolean) => void;
  overrideClasses?: SurveyOverrideClasses;
};

const FocusedSurvey: React.FC<FocusedSurveyProps> = ({
  responseId,
  onSurveyCompletion,
  overrideClasses,
}) => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const classes = useStyles({});
  const [pendingClose, setPendingClose] = React.useState<boolean>(false);

  const {
    survey,
    response,
    onResponseUpdate,
    isLoading,
    isUpdating,
    readError,
    writeError,
  } = useSurveyResponse(responseId);

  React.useEffect(() => {
    if (writeError) {
      notify(formatMessage(messages.writeError), 'error');
    }
  }, [formatMessage, notify, writeError]);

  useCustomCompareEffect(
    () => {
      if (pendingClose && !isUpdating) {
        onSurveyCompletion(response.status === 'completed');
      }
    },
    [isUpdating, pendingClose, onSurveyCompletion],
    ([oldIsUpdating, oldPendingClose], [newIsUpdating, newPendingClose]) =>
      oldIsUpdating === newIsUpdating && oldPendingClose === newPendingClose,
  );

  const isReadOnly = writeError || response?.status === 'completed';

  // Autopopulation needs the subject's patientId; without it the request
  // returns nothing and configured fields stay empty when filling from the
  // subject viewer. Safe for in-progress responses: Survey refuses to apply
  // initial values once a response has been started.
  const patientId = response?.subject?.reference?.replace('Patient/', '');

  return (
    <Box className={classes.paddedContainer}>
      {isLoading || pendingClose ? (
        <DotLoader />
      ) : readError ? (
        <Text>{formatMessage(messages.readError)}</Text>
      ) : (
        <SurveyAutopopulateContainer
          patientId={patientId}
          questionnaire={survey}
          renderWithAutopopulateValues={autopopulateUpdates => (
            <Survey
              autoSave
              isReadOnly={isReadOnly}
              questionnaireResponse={response}
              questionnaire={survey}
              onComplete={isAutoSave => setPendingClose(!isAutoSave)}
              onResponseUpdate={onResponseUpdate}
              overrideClasses={overrideClasses}
              autopopulateValues={autopopulateUpdates}
              subjectId={patientId}
            />
          )}
        />
      )}
    </Box>
  );
};

export default FocusedSurvey;
